'use client';

import { useEffect, useRef, useState } from 'react';
import { getImageMetaData } from '../utils';
import HotspotContainer from '../../hotspot/HotspotContainer';
import { IMAGE_FETCH_PRIORITY } from '../../../constants';
import type { KeyVisualPictureProps } from '../types';
import { IMAGE_LOADING } from '../../image/constants';

/**
 * KeyVisualPicture component - Responsive picture element with hotspot support.
 *
 * Renders a responsive `<picture>` element with device-specific image variants
 * and support for interactive shopping hotspots. Handles image loading states
 * and ensures hotspots are only displayed when the image is fully loaded.
 *
 * @param props - Picture configuration including image data, variants, and hotspot settings
 * @returns Rendered picture element with optional hotspot container
 *
 * @example
 * // Basic responsive picture
 * <KeyVisualPicture
 *   device="desktop"
 *   image={{
 *     url: 'hero.jpg',
 *     alt: 'Hero image',
 *     title: 'Main hero',
 *   }}
 *   pictureData={[
 *     { media: '(min-width: 768px)', srcset: 'hero-lg.jpg 2x, hero-md.jpg 1x' },
 *     { media: '(max-width: 767px)', srcset: 'hero-sm.jpg 1x' },
 *   ]}
 *   hasHotSpots={false}
 * />
 *
 * @example
 * // Picture with interactive hotspots
 * <KeyVisualPicture
 *   device="mobile"
 *   image={{
 *     url: 'product-hero.jpg',
 *     alt: 'Product showcase',
 *     shopableHotspots: {
 *       items: [
 *         { x: 30, y: 40, productId: '123', title: 'Photo Book' },
 *         { x: 60, y: 50, productId: '456', title: 'Calendar' },
 *       ],
 *     },
 *   }}
 *   pictureData={...}
 *   imageVariantsByDeviceSize={{
 *     phoneImage: { '1x': 'mobile.jpg', width: 750, height: 500 },
 *   }}
 *   hasHotSpots={true}
 * />
 */
export function KeyVisualPicture(props: KeyVisualPictureProps) {
  const { image, pictureData, imageVariantsByDeviceSize, device, hasHotSpots } = props;
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [pictureIsLoaded, setPictureIsLoaded] = useState(false);

  const meta = imageVariantsByDeviceSize ? getImageMetaData(imageVariantsByDeviceSize, device, image) : undefined;
  const safeAlt = meta?.alt || '';
  const safeTitle = meta?.title || '';

  const hotspotContent = image?.shopableHotspots;

  useEffect(() => {
    const img = imageRef.current;
    if (!img) return;

    const handleLoad = () => setPictureIsLoaded(true);

    if (img.complete) {
      setPictureIsLoaded(true);
    } else {
      img.addEventListener('load', handleLoad);
    }

    return () => {
      img.removeEventListener('load', handleLoad);
    };
  }, []);

  return (
    <HotspotContainer hotspots={hotspotContent} pictureIsLoaded={pictureIsLoaded}>
      <picture>
        {pictureData.map((img, index) => (
          <source key={index} media={img.media} srcSet={img.srcset} />
        ))}
        <img
          ref={imageRef}
          {...meta}
          alt={safeAlt}
          title={safeTitle}
          loading={IMAGE_LOADING.EAGER}
          fetchPriority={IMAGE_FETCH_PRIORITY.High}
        />
      </picture>
      {hasHotSpots && <div id="hotspot-content-container"></div>}
    </HotspotContainer>
  );
}
